import React, { useEffect } from 'react'
import { Box } from '@mui/material'
import {
  Window,
  MessageList,
  MessageInput,
  useChannelStateContext,
  MessageInputFlat,
  useMessageContext,
  MessageSimple,
  useChannelActionContext,
  useMessageComposer,
  Attachment,
  UnsupportedAttachment,
} from 'stream-chat-react'
import ChannelHeader from './channel-header'
import useQueryString from 'lib/hooks/use-query-string'
import { useMediaInfoFactory } from 'lib/hooks/use-media-info-factory'
import { useVideoAnalysis } from 'lib/hooks/use-video-analysis'
import {
  GroupEventAttachmentCard,
  LessonTypeAttachmentCard,
} from './components'
import {
  OUR_TYPES,
  prepareCustomAttachment,
  replaceMentions,
  fileUploadRequestOverride,
} from './utils'
import { useAttachmentContext } from './use-attachment-context'
import PlaceholderWithQrCode from './components/placeholder-with-qr-code'
import { useAuth } from 'lib/auth'

/*
 * In stream-chat-react v13, custom attachment types are no longer routed through
 * CardContainer. Instead, anything that isn't a known built-in type (scraped URL
 * preview, video, audio, file, etc.) falls through to UnsupportedAttachmentContainer.
 * We override UnsupportedAttachment to handle our custom types. Note the API change:
 * the override receives { attachment } (the whole object) rather than spread props.
 */
const CustomUnsupportedAttachment = ({ attachment }) => {
  switch (attachment?.type) {
    case 'pga_group_event':
      return <GroupEventAttachmentCard {...attachment} />
    case 'pga_lesson_type':
      return <LessonTypeAttachmentCard {...attachment} />
    case 'pga_student_library_upload':
      // This is a stopgap effort. In the future, we may instead support displaying the video.
      return <PlaceholderWithQrCode />
    default:
      return <UnsupportedAttachment attachment={attachment} />
  }
}

export const CustomAttachment = props => {
  return (
    <Attachment
      {...props}
      UnsupportedAttachment={CustomUnsupportedAttachment}
    />
  )
}

export const CustomMessage = () => {
  let { message } = useMessageContext()
  const { user } = useAuth()
  const mediaInfoFactory = useMediaInfoFactory()
  const { attachments } = message
  const isMyMessage = message.user?.id === user?.externalId
  const nonCompliantVideos = useVideoAnalysis(
    attachments,
    mediaInfoFactory,
    !isMyMessage,
  )
  let customAttachment = false
  let hasUrlAttachment = false

  // Listed Group Events may be scraped and generate a URL preview. This causes TWO preview cards to appear.
  // This checks for the presence of our custom attachment and an URL attachment and prioritizes ours.
  attachments.length > 0 &&
    attachments.forEach(att => {
      if (OUR_TYPES.includes(att.type)) customAttachment = att
      if (att.og_scrape_url) hasUrlAttachment = true
    })

  if (customAttachment && hasUrlAttachment) {
    message.attachments = [customAttachment]
  }

  if (message.mentioned_users.length > 0) {
    message = replaceMentions({ message: message })
  }

  return (
    <>
      {nonCompliantVideos.length > 0 && (
        <Box maxWidth="375px">
          <PlaceholderWithQrCode type="unsupported-video" />
        </Box>
      )}
      <MessageSimple message={message} />
    </>
  )
}

const UserMessageInput = () => {
  const messageComposer = useMessageComposer()
  const { shared } = useQueryString()
  const prompt = 'Check out this link for booking info: '

  useEffect(() => {
    if (shared) {
      messageComposer?.textComposer?.setText(prompt + shared)
    }
  }, [shared, messageComposer])

  return (
    <Box
      sx={{
        position: 'sticky',
        bottom: 0,
        zIndex: 1,
      }}
    >
      <MessageInputFlat />
    </Box>
  )
}

export const ChannelInner = () => {
  const { sendMessage } = useChannelActionContext()
  const { shared, attType, id } = useAttachmentContext()
  const { channel } = useChannelStateContext()
  const messageComposer = useMessageComposer()

  useEffect(() => {
    const customUpload = async file => {
      return fileUploadRequestOverride(file, channel)
    }
    messageComposer?.attachmentManager?.setCustomUploadFn(customUpload)
  }, [channel, messageComposer])

  const overrideSubmitHandler = async ({
    localMessage,
    message,
    sendOptions,
  }) => {
    const customAttachment = prepareCustomAttachment({
      message: localMessage,
      shared: shared,
      attType: attType,
      id: id,
    })
    if (customAttachment) {
      localMessage.attachments = [
        ...(localMessage.attachments || []),
        customAttachment,
      ]
    }

    if (sendMessage) {
      await sendMessage({ localMessage, message, options: sendOptions })
    }
  }

  return (
    <Window>
      <ChannelHeader />
      <MessageList style={{ textAlign: 'left !important' }} />
      <MessageInput
        focus={false}
        Input={UserMessageInput}
        overrideSubmitHandler={overrideSubmitHandler}
        maxRows={12}
      />
    </Window>
  )
}
